import styled from "styled-components"
import React from 'react'

const experienceData = [
  {
    role: "Full Stack Engineer",
    company: "Srotas Health · UK (Remote)",
    period: "Jun 2025 - Present",
    image: "./gridImages/server.svg",
    shape: "shape-hex",
    points: [
      "Built the first end-to-end product from scratch across frontend and backend.",
      "Led frontend implementation standards and mentored engineers.",
      "Platform growth helped secure 3+ clients in an early-stage business."
    ]
  },
  {
    role: "Founder & Software Engineer",
    company: "Campus Assembly",
    period: "Jan 2024 - May 2024",
    image: "./gridImages/ideas.svg",
    shape: "shape-cone",
    points: [
      "Designed and deployed a SaaS education management platform.",
      "Worked with React, Next.js, Node.js, PostgreSQL, and Google Cloud Run.",
      "Implemented SEO and performance upgrades that improved search visibility."
    ]
  },
  {
    role: "Front-End Developer",
    company: "Esmito Solutions · Mumbai",
    period: "Nov 2022 - Dec 2023",
    image: "./gridImages/logo22.svg",
    shape: "shape-orbit",
    points: [
      "Built an operations dashboard for EV battery swap workflows.",
      "Contributed to Flutter apps for Android and iOS releases.",
      "Delivered product features that improved retention and daily engagement."
    ]
  },
  {
    role: "Front-End Developer",
    company: "InterviewerAI · Singapore (Remote)",
    period: "Apr 2022 - Oct 2022",
    image: "./gridImages/experience.svg",
    shape: "shape-cube",
    points: [
      "Migrated class components to functional components for maintainability.",
      "Implemented adaptive responsive design and WCAG-oriented UI refinements.",
      "Built JavaScript data visualizations for product insights."
    ]
  },
  {
    role: "Front-End Developer",
    company: "IshopAtoZ · Bangalore (Remote)",
    period: "Oct 2021 - Mar 2022",
    image: "./gridImages/improve.svg",
    shape: "shape-grid",
    points: [
      "Built responsive interfaces for React and React Native app experiences.",
      "Implemented SEO fundamentals including metadata and lazy loading.",
      "Collaborated closely with design and product to ship polished UI." 
    ]
  }
];

const skillGroups = [
  {
    title: "Web",
    items: ["JavaScript", "TypeScript", "React", "Next.js", "Redux", "Tailwind CSS", "SCSS"]
  },
  {
    title: "Backend",
    items: ["Node.js", "Express", "NestJS", "FastAPI", "PostgreSQL", "Firebase", "REST APIs"]
  },
  {
    title: "Mobile & Cloud",
    items: ["React Native", "Expo", "Flutter", "AWS", "GCP", "Azure", "Cloud Run", "CI/CD", "Docker"]
  },
  {
    title: "AI",
    items: ["Generative AI", "RAG", "LangChain", "Vector Databases", "Prompt Engineering", "Model Bias Analysis"]
  }
];

const aiAwareness = [
  "Uses AI coding tools for generation, debugging, refactoring, and documentation workflows.",
  "Writes iterative prompts and validates generated code for production quality.",
  "Integrates LLM APIs in frontend products with UX considerations for streaming responses.",
  "Balances AI productivity gains with performance, security, and maintainability standards."
];

const collaborationStrengths = [
  "Startup-first mindset: high ownership, fast iteration, and comfort with ambiguity.",
  "Strong communication across product, design, and engineering stakeholders.",
  "Coordinates effectively in cross-functional teams to align execution with common goals.",
  "Works in harmony with teams while also stepping up to lead when needed.",
  "Adapts quickly to new challenges and contributes across the full product lifecycle."
];

function Grid(){
  return (
    <GridStyle>
      <section className="section intro">
        <h2>Professional Summary</h2>
        <p>
          Results-driven full stack engineer with 4+ years of experience building scalable web and
          mobile applications, AI-enabled product features, and cloud-native systems in fast-moving teams.
        </p>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Experience Journey</h2>
          <p>Each role uses a different geometric visual language to represent product stage and ownership.</p>
        </div>
        <div className="experience-grid">
          {experienceData.map((item) => (
            <article className={`experience-card ${item.shape}`} key={`${item.role}-${item.company}`}>
              <div className="shape-layer" aria-hidden="true" />
              <div className="experience-body">
                <p className="period">{item.period}</p>
                <h3>{item.role}</h3>
                <h4>{item.company}</h4>
                <ul>
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
              <img className="icons-svg" alt="" src={item.image}/>
            </article>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Skills Snapshot</h2>
        </div>
        <div className="skills-grid">
          {skillGroups.map((group) => (
            <article className="skill-card" key={group.title}>
              <h3>{group.title}</h3>
              <div className="pill-wrap">
                {group.items.map((item) => (
                  <span className="pill" key={item}>{item}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section split">
        <article className="panel">
          <h2>AI Awareness</h2>
          <ul>
            {aiAwareness.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>

        <article className="panel">
          <h2>Education & Certifications</h2>
          <p><b>B.Tech, Computer Science & Engineering</b></p>
          <p>Aliah University, Kolkata · Jul 2017 - Aug 2021</p>
          <div className="cert-list">
            <span>Generative AI - Udacity</span>
            <span>AI Product Manager - Udacity</span>
            <span>Server-side Development with NodeJS - Coursera</span>
            <span>Front End Web Developer - Udacity</span>
          </div>
        </article>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Startup Mindset & Team Collaboration</h2>
          <p>
            Along with technical depth, I bring energy for startup environments where communication,
            ownership, and coordinated execution define product success.
          </p>
        </div>
        <article className="panel collaboration-panel">
          <ul>
            {collaborationStrengths.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
      </section>
    </GridStyle>
  )
}

const GridStyle = styled.div`
  --ink: #0f2136;
  --muted: #5a6f8b;
  --card: #ffffff;
  --line: rgba(15, 33, 64, 0.1);
  --accent: #005fbb;
  --accent-soft: #ecf4ff;

  width: min(1120px, 92%);
  margin: 0 auto;
  display: grid;
  gap: 1.4rem;
  padding-bottom: 2rem;

  .section {
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.96);
    border-radius: 24px;
    padding: 1.3rem;
    box-shadow: 0 18px 42px rgba(15, 33, 64, 0.08);
  }

  .intro {
    background: linear-gradient(140deg, rgba(237, 244, 255, 0.94), rgba(244, 250, 255, 0.98));
  }

  h2 {
    margin: 0;
    font-family: "Inter", "Segoe UI", sans-serif;
    color: var(--ink);
  }

  .section-head p,
  .intro p,
  .panel p,
  li {
    color: var(--muted);
    font-family: "Inter", "Segoe UI", sans-serif;
    line-height: 1.65;
  }

  .section-head p {
    margin-top: 0.5rem;
  }

  .experience-grid {
    display: grid;
    gap: 1.1rem;
    margin-top: 1rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .experience-card {
    position: relative;
    overflow: hidden;
    border-radius: 22px;
    border: 1px solid var(--line);
    background: var(--card);
    display: grid;
    grid-template-columns: 1fr 100px;
    gap: 1rem;
    padding: 1.2rem;
  }

  .shape-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.55;
  }

  .shape-hex .shape-layer {
    background:
      radial-gradient(circle at 82% 20%, rgba(0, 95, 187, 0.18), transparent 24%),
      repeating-linear-gradient(120deg, transparent 0 18px, rgba(0, 95, 187, 0.12) 18px 20px);
  }

  .shape-cone .shape-layer {
    background:
      linear-gradient(140deg, rgba(0, 95, 187, 0.1), transparent 45%),
      radial-gradient(circle at 90% 88%, rgba(0, 95, 187, 0.15), transparent 32%);
  }

  .shape-orbit .shape-layer {
    background:
      radial-gradient(circle at 80% 20%, transparent 16px, rgba(0, 95, 187, 0.16) 17px 19px, transparent 20px),
      radial-gradient(circle at 80% 20%, transparent 30px, rgba(0, 95, 187, 0.12) 31px 33px, transparent 34px),
      linear-gradient(155deg, rgba(0, 95, 187, 0.08), transparent 58%);
  }

  .shape-cube .shape-layer {
    background:
      linear-gradient(45deg, rgba(8, 52, 112, 0.12), transparent 44%),
      linear-gradient(135deg, transparent 0 65%, rgba(13, 71, 148, 0.09) 66% 100%);
  }

  .shape-grid .shape-layer {
    background:
      linear-gradient(rgba(0, 95, 187, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 95, 187, 0.08) 1px, transparent 1px);
    background-size: 24px 24px;
  }

  .experience-body {
    position: relative;
    z-index: 1;
  }

  .period {
    margin: 0;
    font-size: 0.78rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--accent);
  }

  h3 {
    margin: 0.35rem 0 0.2rem;
    font-family: "Inter", "Segoe UI", sans-serif;
    font-size: 1.05rem;
  }

  h4 {
    margin: 0;
    font-size: 0.92rem;
    color: #253b58;
    font-family: "Inter", "Segoe UI", sans-serif;
    font-weight: 600;
  }

  ul {
    margin: 0.75rem 0 0;
    padding-left: 1rem;
  }

  li {
    margin-bottom: 0.45rem;
    font-size: 0.92rem;
  }

  .icons-svg {
    position: relative;
    z-index: 1;
    width: 84px;
    align-self: center;
    justify-self: end;
    opacity: 0.9;
  }

  .skills-grid {
    margin-top: 1rem;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem;
  }

  .skill-card {
    border: 1px solid var(--line);
    border-radius: 18px;
    padding: 1.1rem;
    background: var(--surface);
  }

  .pill-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.7rem;
  }

  .pill {
    background: var(--accent-soft);
    color: #0f2136;
    border: 1px solid rgba(0, 95, 187, 0.18);
    border-radius: 999px;
    padding: 0.32rem 0.8rem;
    font-size: 0.8rem;
    font-family: "Inter", "Segoe UI", sans-serif;
    font-weight: 700;
  }

  .split {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .panel {
    border: 1px solid var(--line);
    border-radius: 18px;
    padding: 1.1rem;
    background: var(--surface);
  }

  .collaboration-panel {
    margin-top: 1rem;
    background: linear-gradient(180deg, rgba(232, 243, 255, 0.9), rgba(252, 252, 255, 0.95));
  }

  .cert-list {
    margin-top: 0.8rem;
    display: grid;
    gap: 0.5rem;
  }

  .cert-list span {
    display: inline-block;
    border-left: 3px solid var(--accent);
    padding-left: 0.7rem;
    color: #223651;
    font-family: "Inter", "Segoe UI", sans-serif;
  }

  @media screen and (max-width: 980px) {
    .experience-grid,
    .skills-grid,
    .split {
      grid-template-columns: 1fr;
    }

    .experience-card {
      grid-template-columns: 1fr;
    }

    .icons-svg {
      justify-self: start;
      width: 68px;
    }
  }

  @media screen and (max-width: 650px) {
    .section {
      border-radius: 18px;
      padding: 1m;
    }

    h2 {
      font-size: 1.3rem;
    }

    li {
      font-size: 0.9rem;
    }
  }
`

export default Grid
